/* One mobile resume owner. Safari can restore scroll after pageshow and
   viewport events have finished, so restoration ends on user intent, not time. */
(() => {
  const mobile = matchMedia('(max-width:1200px)');
  const html = document.documentElement;
  const body = document.body;
  const main = document.getElementById('MainContent');
  if (!main || !body.classList.contains('ak-site-frame-enabled')) return;
  const inner = body.classList.contains('ak-page-inner');
  const owner = inner ? main : (document.scrollingElement || html);
  const key = 'ak-mobile-page-scroll-v3:' + location.pathname + location.search;
  const navigation = performance.getEntriesByType('navigation')[0];
  let saved = 0;
  if (navigation?.type === 'back_forward' && !(body.classList.contains('ak-page-index') && window.AK_HOME_INTRO?.returnEntrance)) {
    try {
      const record = history.state?.akMobileScroll;
      const stored = record?.key === key ? record : JSON.parse(sessionStorage.getItem(key));
      if (Number.isFinite(stored?.top) && stored.top >= 0) saved = stored.top;
    } catch (_) {}
  }
  let suspended = false;
  let guarded = true;
  let frame = 0;
  let wroteRoot = false;
  const zoomed = () => window.AK_NATIVE_ZOOM?.isZooming() || (window.visualViewport && Math.abs(visualViewport.scale - 1) > .01);
  const editing = () => {
    const viewport = window.visualViewport;
    const field = document.activeElement?.matches('textarea,[contenteditable="true"],input:not([type="checkbox"]):not([type="radio"]):not([type="button"]):not([type="submit"]):not([type="reset"]):not([type="range"]):not([type="hidden"])');
    // A field can retain focus after Safari dismisses its keyboard. That
    // stale focus must not disable restoration on the next browser visit.
    return field && viewport && html.clientHeight - viewport.height > 120;
  };
  function persist() {
    const record = {key, top:saved};
    try { sessionStorage.setItem(key, JSON.stringify(record)); } catch (_) {}
    try { history.replaceState({...history.state, akMobileScroll:record}, ''); } catch (_) {}
  }
  function markState() {
    html.dataset.akMobileResumeState = suspended ? 'suspended' : guarded ? 'guarded' : 'active';
    html.dataset.akMobileScrollOwner = inner ? 'content' : 'document';
  }
  function resetDocument() {
    if (!inner || zoomed() || editing()) return;
    // Always issue this once on resume, including when scrollY is zero:
    // WebKit's visual viewport can retain a pan separate from document scroll.
    if (!wroteRoot || scrollX || scrollY || html.scrollTop || body.scrollTop) {
      wroteRoot = true;
      window.scrollTo({top:0, left:0, behavior:'instant'});
      html.scrollTop = body.scrollTop = 0;
    }
  }
  function repair() {
    frame = 0;
    if (!mobile.matches || suspended || document.hidden || zoomed() || editing()) return;
    resetDocument();
    if (!guarded) return;
    const target = Math.min(saved, Math.max(0, owner.scrollHeight - owner.clientHeight));
    if (Math.abs(owner.scrollTop - target) > .5) owner.scrollTop = target;
  }
  function queue() {
    if (mobile.matches && !zoomed() && !frame) frame = requestAnimationFrame(repair);
  }
  function resume() {
    if (!mobile.matches || document.hidden || zoomed()) return;
    suspended = false;
    guarded = true;
    wroteRoot = false;
    markState();
    repair();
    queue();
    window.dispatchEvent(new CustomEvent('ak:mobile-resume'));
  }
  function suspend() {
    if (!mobile.matches || suspended) return;
    // Freeze the last active snapshot. Do not sample Safari's suspension pan.
    suspended = true;
    guarded = true;
    cancelAnimationFrame(frame);
    frame = 0;
    persist();
    markState();
  }
  function intent(event) {
    if (!mobile.matches || suspended || document.hidden || zoomed() || !event.isTrusted) return;
    // Reconcile any late restore before the first new gesture starts. Then
    // all native scrolling, momentum, focus and keyboard behaviour is free.
    if (guarded) repair();
    guarded = false;
    saved = Math.max(0, owner.scrollTop);
    markState();
  }
  ['touchstart','pointerdown','wheel','keydown'].forEach(name =>
    document.addEventListener(name, intent, {capture:true, passive:true}));
  document.addEventListener('scroll', event => {
    if (!mobile.matches || suspended || document.hidden || zoomed()) return;
    if (event.target === owner || (!inner && event.target === document)) {
      if (guarded) queue();
      else saved = Math.max(0, owner.scrollTop);
    } else if (inner && [document,html,body].includes(event.target)) queue();
  }, {capture:true, passive:true});
  document.addEventListener('scrollend', event => {
    if (event.target === owner || event.target === document) {
      if (guarded) queue(); else if (!suspended) persist();
    }
  }, {capture:true, passive:true});
  document.addEventListener('visibilitychange', () => document.hidden ? suspend() : resume());
  window.addEventListener('pagehide', suspend);
  window.addEventListener('ak:home-return-start', () => {
    saved = 0;
    guarded = false;
    wroteRoot = false;
    persist();
  });
  // The one-shot product preview is intentional scrolling, not a WebKit
  // restoration drift. Let its native scroll events update the saved position.
  window.addEventListener('ak:product-preview-start', event => {
    if(!mobile.matches || suspended || event.detail?.owner!==owner ||
      !body.classList.contains('ak-page-product') || navigation?.type==='back_forward')return;
    guarded=false;
    saved=Math.max(0,owner.scrollTop);
    markState();
  });
  window.addEventListener('ak:product-preview-end', event => {
    if(!mobile.matches || suspended || event.detail?.owner!==owner)return;
    saved=Math.max(0,owner.scrollTop);
    persist();
    markState();
  });
  window.addEventListener('pageshow', resume);
  window.addEventListener('blur', suspend);
  window.addEventListener('focus', () => { if (suspended) resume(); });
  window.addEventListener('resize', queue, {passive:true});
  window.visualViewport?.addEventListener('resize', queue, {passive:true});
  window.visualViewport?.addEventListener('scroll', () => { wroteRoot = false; queue(); }, {passive:true});
  ['ak:global-frame-ready','ak:home-frame-ready','ak:boot-revealed','ak:layout-refresh'].forEach(name =>
    window.addEventListener(name, queue));
  const sizes = new ResizeObserver(queue);
  [main, ...main.children].forEach(element => sizes.observe(element));
  main.addEventListener('load', queue, true);
  new MutationObserver(queue).observe(main, {childList:true, subtree:true});
  document.fonts?.ready.then(queue);
  mobile.addEventListener('change', () => {
    if (mobile.matches) resume(); else { cancelAnimationFrame(frame); frame = 0; }
  });
  markState();
  resume();
})();
